import { UPDATE_STAKEHOLDERS } from "@lib/graphql/mutations";
import { ChevronDown, ChevronUp, Minus, Pencil, Plus, Save, Users, X } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";

const STAKEHOLDER_ROLES = [
  { key: "hiring_manager", label: "Hiring Manager" },
  { key: "interviewer", label: "Interviewer" },
  { key: "role_definer", label: "Role definer" },
  { key: "approver", label: "Approver" },
  { key: "uncategorised", label: "Uncategorised" },
] as const;

function roleLabel(key: string): string {
  return STAKEHOLDER_ROLES.find((r) => r.key === key)?.label ?? "Uncategorised";
}

export interface Stakeholder {
  id?: string;
  stakeholderRole: string;
  userId: string;
  userName: string | null;
}

interface TeamMember {
  id: string;
  fullName: string;
}

interface StakeholdersEditorProps {
  campaignId: string;
  initial: Stakeholder[];
  teamMembers: TeamMember[];
  onSaved: () => void;
}

const emptyRow = (): Stakeholder => ({
  stakeholderRole: "uncategorised",
  userId: "",
  userName: null,
});

export function StakeholdersEditor({
  campaignId,
  initial,
  teamMembers,
  onSaved,
}: StakeholdersEditorProps) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Stakeholder[]>(initial);
  const [{ fetching: saving }, updateStakeholders] = useMutation(UPDATE_STAKEHOLDERS);

  const hasTeam = teamMembers.length > 0;

  const addRow = () => setRows([...rows, emptyRow()]);

  const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));

  const updateRow = (index: number, patch: Partial<Stakeholder>) =>
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const moveRow = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
  };

  const handleSave = async () => {
    const filtered = rows.filter((r) => r.userId !== "");
    await updateStakeholders({
      input: {
        campaignId,
        stakeholders: filtered.map((r) => ({
          userId: r.userId,
          stakeholderRole: r.stakeholderRole,
        })),
      },
    });
    setEditing(false);
    onSaved();
  };

  const handleCancel = () => {
    setRows(initial);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-text-secondary" />
            <h3 className="text-sm font-medium text-text-secondary">Stakeholders</h3>
          </div>
          {hasTeam && (
            <button
              type="button"
              onClick={() => {
                setRows(initial.length > 0 ? initial : [emptyRow()]);
                setEditing(true);
              }}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
            >
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>
        {!hasTeam ? (
          <p className="text-sm text-text-muted">
            Add team members to the campaign before assigning stakeholders.
          </p>
        ) : initial.length === 0 ? (
          <p className="text-sm text-text-muted">No stakeholders added yet</p>
        ) : (
          <div className="space-y-1">
            {initial.map((s) => (
              <div
                key={s.id ?? `${s.userId}-${s.stakeholderRole}`}
                className="flex flex-wrap items-baseline gap-2 text-sm"
              >
                <span className="text-text-primary">{s.userName ?? "Unknown user"}</span>
                <span className="text-text-faint">&mdash; {roleLabel(s.stakeholderRole)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Stakeholders</h3>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((row, i) => {
          const key = `stakeholder-${i}`;
          return (
            <div key={key} className="flex items-center gap-2">
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => moveRow(i, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                  className="rounded p-0.5 text-text-faint hover:bg-card-hover hover:text-text-secondary disabled:opacity-30"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => moveRow(i, 1)}
                  disabled={i === rows.length - 1}
                  aria-label="Move down"
                  className="rounded p-0.5 text-text-faint hover:bg-card-hover hover:text-text-secondary disabled:opacity-30"
                >
                  <ChevronDown size={12} />
                </button>
              </div>
              <select
                value={row.userId}
                onChange={(e) => {
                  const userId = e.target.value;
                  const userName = teamMembers.find((m) => m.id === userId)?.fullName ?? null;
                  updateRow(i, { userId, userName });
                }}
                aria-label="User"
                className="flex-1 rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">— Select user —</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName}
                  </option>
                ))}
              </select>
              <select
                value={row.stakeholderRole}
                onChange={(e) => updateRow(i, { stakeholderRole: e.target.value })}
                aria-label="Role"
                className="rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {STAKEHOLDER_ROLES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeRow(i)}
                aria-label="Remove stakeholder"
                className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
              >
                <Minus size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"
      >
        <Plus size={12} /> Add stakeholder
      </button>
    </div>
  );
}
